/**
 * The route table, and the three questions that have to be answered before any
 * of it can be shown: has this server been set up, who is signed in, and does
 * the owner still owe us an onboarding pass.
 *
 * All three are settled in one gate rather than inside each page, so no screen
 * has to defend itself against being rendered at the wrong moment — and, in
 * particular, so nobody is shown a login form for a fraction of a second while
 * their session is still being confirmed.
 */

import { useEffect } from 'react'
import type { ReactElement } from 'react'
import { Link, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'

import { needsMfaEnrollment, useAuth } from '@/api/auth'
import { useSetupStatus } from '@/api/queries'
import { Button, FullPageMessage, FullPageSpinner } from '@/components/ui'
import { resetRoomLabels } from '@/components/Sidebar'
import { CallProvider } from '@/components/CallProvider'
import { useActivityBridge } from '@/hooks/useActivityBridge'

import { AccountPage } from '@/routes/AccountPage'
import { OverviewPage } from '@/routes/OverviewPage'
import { AdminLayout } from '@/routes/admin/AdminLayout'
import { RoomView } from '@/components/RoomView'
import { AppShell } from '@/routes/AppShell'
import { CodeRegistrationPage, InvitePage } from '@/routes/InvitePage'
import { LoginPage } from '@/routes/LoginPage'
import { OnboardingPage } from '@/routes/OnboardingPage'
import { RouteErrorBoundary } from '@/routes/RouteErrorBoundary'
import { SearchPage } from '@/routes/SearchPage'
import { SetupPage } from '@/routes/SetupPage'

function normalisePath(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

// The only screens that mean anything without a session. Everything else is a
// view of this server's contents and needs one.
const PUBLIC_PATHS = ['/login', '/setup', '/register']

function isPublic(path: string): boolean {
  return PUBLIC_PATHS.includes(path) || path.startsWith('/invite/')
}

function Gate(): ReactElement {
  const location = useLocation()
  const setup = useSetupStatus()
  const { user, loading, server, sessionUnavailable, refresh } = useAuth()

  const path = normalisePath(location.pathname)

  // `useSetupStatus` stays pending across its own retries, so this covers a
  // slow server as well as a fast one.
  if (setup.isPending || loading) {
    return <FullPageSpinner label="Loading relaii" />
  }

  // An unreachable server leaves `data` undefined. Carrying on is deliberate:
  // the cached store can still serve a signed-in user offline, and blocking
  // here would take that away to guard against a case — an unclaimed server
  // that is also unreachable — which cannot happen.
  if (setup.data?.setup_required === true) {
    return path === '/setup' ? <Outlet /> : <Navigate to="/setup" replace />
  }

  if (sessionUnavailable && !isPublic(path)) {
    return <FullPageMessage title="Your Space is temporarily unavailable"
      description="Your saved sign-in is still here. Check your connection and try again."
      actions={<Button onClick={() => void refresh()}>Try again</Button>} />
  }

  // An owner already exists, so the setup screen can only fail from here.
  if (path === '/setup') {
    return <Navigate to="/" replace />
  }

  // Signed out. The destination is carried through the redirect so that a
  // bookmarked room, or a link someone was sent, still lands where it meant to
  // once they have signed in.
  if (user === null && !isPublic(path)) {
    const from = `${location.pathname}${location.search}`
    return <Navigate to="/login" replace state={{ from }} />
  }

  // Signed in, so the login form has nothing left to offer.
  if (user !== null && path === '/login') {
    return (
      <Navigate to={needsMfaEnrollment(user, server) ? '/account' : '/'} replace />
    )
  }

  const needsMfa = needsMfaEnrollment(user, server)

  // Rooms, admin and the socket refuse until the second factor is confirmed.
  // Enrolment lives outside AppShell so a 403 on `/rooms` cannot strand the UI.
  if (needsMfa && path !== '/account') {
    return <Navigate to="/account" replace />
  }

  const owesOnboarding =
    user !== null && user.is_owner && user.onboarding_completed_at === null

  if (owesOnboarding && path !== '/onboarding' && !needsMfa) {
    return <Navigate to="/onboarding" replace />
  }

  return <Outlet />
}

function NotFoundPage(): ReactElement {
  return (
    <FullPageMessage
      title="Page not found"
      description="That address does not lead anywhere in this server."
      actions={<Link to="/">Back to your rooms</Link>}
    />
  )
}

/**
 * Everything a signed-in person sees, whichever screen it is.
 *
 * The window title, the desktop badge and notifications follow the account,
 * not the conversation columns, so they are mounted here rather than in
 * AppShell: somebody reading the admin pages or their account settings is
 * still somebody a mention should reach. The direct-message label registry is
 * emptied here too, when the account changes or goes — a label is the *other*
 * participant's name, and the next account has a different other.
 */
function AuthenticatedLayout(): ReactElement {
  const { user } = useAuth()
  const userId = user?.id ?? null

  useActivityBridge()

  useEffect(() => {
    return () => {
      resetRoomLabels()
    }
  }, [userId])

  return (
    <CallProvider>
      <Outlet />
    </CallProvider>
  )
}

/** One conversation, with its own failure contained to the conversation column. */
function RoomRoute(): ReactElement {
  const location = useLocation()

  return (
    <RouteErrorBoundary resetKey={location.pathname}>
      <RoomView />
    </RouteErrorBoundary>
  )
}

export function App(): ReactElement {
  return (
    <Routes>
      <Route element={<Gate />}>
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<CodeRegistrationPage />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route element={<AuthenticatedLayout />}>
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/admin/*" element={<AdminLayout />} />
          {/*
            AppShell is a layout route, not a page: it owns the three columns and
            renders the conversation into its <Outlet />. Keeping it above the room
            routes is what stops the shell — and the message list's scroll position
            — from being torn down and rebuilt on every room switch.
          */}
          <Route element={<AppShell />}>
            <Route path="/" element={<Navigate to="/overview" replace />} />
            <Route path="/overview" element={<OverviewPage />} />
            <Route path="/overview/:tab" element={<OverviewPage />} />
            {/* Where the magnifier in every room header goes, query and all. */}
            <Route path="/search" element={<SearchPage />} />
            <Route path="/activity" element={<Navigate to="/overview/activity" replace />} />
            <Route path="/saved" element={<Navigate to="/overview/saved" replace />} />
            <Route path="/files" element={<Navigate to="/overview/files" replace />} />
            <Route path="/rooms/:roomId" element={<RoomRoute />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}
